import { useState, useEffect, useRef } from "react";

// ─── Inline styles & CSS variables injected via <style> ───────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --green-deep: #1a3a2a;
      --green-mid: #2d6a4f;
      --green-bright: #52b788;
      --green-light: #b7e4c7;
      --cream: #faf7f0;
      --warm-white: #fff9f0;
      --amber: #e09f3e;
      --amber-light: #f9c74f;
      --terracotta: #c1440e;
      --text-dark: #1a1a1a;
      --text-mid: #4a4a4a;
      --text-light: #8a8a8a;
      --shadow-soft: 0 4px 30px rgba(26,58,42,0.10);
      --shadow-card: 0 8px 40px rgba(26,58,42,0.13);
      --radius: 18px;
      --radius-sm: 10px;
    }

    html { scroll-behavior: smooth; }

    body {
      font-family: 'DM Sans', sans-serif;
      background: var(--cream);
      color: var(--text-dark);
      overflow-x: hidden;
    }

    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: var(--cream); }
    ::-webkit-scrollbar-thumb { background: var(--green-bright); border-radius: 3px; }

    /* Animations */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideIn {
      from { opacity: 0; transform: translateX(-24px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes float {
      0%,100% { transform: translateY(0); }
      50%     { transform: translateY(-10px); }
    }
    @keyframes pulse {
      0%,100% { transform: scale(1); }
      50%     { transform: scale(1.05); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes ticker {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    @keyframes shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }
    @keyframes ripple {
      0%   { transform: scale(0); opacity: .6; }
      100% { transform: scale(2.5); opacity: 0; }
    }
    @keyframes leafSway {
      0%,100% { transform: rotate(-5deg); }
      50%      { transform: rotate(5deg); }
    }
    @keyframes cartBounce {
      0%,100% { transform: scale(1); }
      30%      { transform: scale(1.3) rotate(-10deg); }
      60%      { transform: scale(.9) rotate(5deg); }
    }

    .animate-fade-up   { animation: fadeUp .6s ease both; }
    .animate-slide-in  { animation: slideIn .5s ease both; }
    .animate-float     { animation: float 3s ease-in-out infinite; }
    .animate-pulse     { animation: pulse 2s ease-in-out infinite; }
    .animate-leaf      { animation: leafSway 4s ease-in-out infinite; }

    .delay-1 { animation-delay: .1s; }
    .delay-2 { animation-delay: .2s; }
    .delay-3 { animation-delay: .3s; }
    .delay-4 { animation-delay: .4s; }
    .delay-5 { animation-delay: .5s; }

    /* Ticker */
    .ticker-wrap { background: var(--green-deep); color: #fff; overflow: hidden; white-space: nowrap; padding: 10px 0; }
    .ticker-track { display: inline-flex; animation: ticker 28s linear infinite; }
    .ticker-item  { padding: 0 48px; font-size: 13px; font-weight: 500; letter-spacing: .5px; }

    /* Nav */
    .nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
      background: rgba(250,247,240,.92); backdrop-filter: blur(16px);
      border-bottom: 1px solid rgba(82,183,136,.15);
      transition: box-shadow .3s;
    }
    .nav.scrolled { box-shadow: var(--shadow-soft); }
    .nav-inner { max-width: 1280px; margin: 0 auto; padding: 0 24px; height: 72px; display: flex; align-items: center; justify-content: space-between; }
    .logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
    .logo-icon { width: 40px; height: 40px; background: var(--green-mid); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; animation: float 3s ease-in-out infinite; }
    .logo-text { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 900; color: var(--green-deep); }
    .logo-text span { color: var(--amber); }
    .nav-links { display: flex; gap: 32px; list-style: none; }
    .nav-links a { text-decoration: none; color: var(--text-mid); font-weight: 500; font-size: 14px; transition: color .2s; position: relative; }
    .nav-links a::after { content:''; position: absolute; bottom: -3px; left: 0; width: 0; height: 2px; background: var(--green-bright); transition: width .3s; border-radius: 2px; }
    .nav-links a:hover { color: var(--green-mid); }
    .nav-links a:hover::after { width: 100%; }
    .nav-actions { display: flex; align-items: center; gap: 12px; }
    .btn-cart {
      position: relative; background: var(--green-mid); color: #fff;
      border: none; border-radius: 50px; padding: 10px 20px;
      font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600;
      cursor: pointer; display: flex; align-items: center; gap: 8px;
      transition: background .2s, transform .2s;
    }
    .btn-cart:hover { background: var(--green-deep); transform: translateY(-1px); }
    .btn-cart.bounce .cart-icon { animation: cartBounce .5s ease; }
    .cart-badge {
      position: absolute; top: -6px; right: -6px;
      background: var(--amber); color: var(--green-deep);
      border-radius: 50%; width: 20px; height: 20px;
      font-size: 11px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
    }

    /* Hero */
    .hero {
      min-height: 100vh; display: flex; align-items: center;
      background: linear-gradient(135deg, #e8f5e9 0%, #fff9f0 50%, #f1faf4 100%);
      padding-top: 72px; position: relative; overflow: hidden;
    }
    .hero::before {
      content: ''; position: absolute; inset: 0;
      background-image: radial-gradient(circle at 20% 50%, rgba(82,183,136,.12) 0%, transparent 60%),
                        radial-gradient(circle at 80% 30%, rgba(224,159,62,.10) 0%, transparent 50%);
    }
    .hero-blob {
      position: absolute; right: -100px; top: 50%; transform: translateY(-50%);
      width: 600px; height: 600px; border-radius: 60% 40% 70% 30%/50% 60% 40% 50%;
      background: linear-gradient(135deg, rgba(82,183,136,.2), rgba(45,106,79,.15));
      animation: float 6s ease-in-out infinite;
    }
    .hero-inner { max-width: 1280px; margin: 0 auto; padding: 0 24px; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; position: relative; z-index: 1; }
    .hero-badge { display: inline-flex; align-items: center; gap: 8px; background: rgba(82,183,136,.15); color: var(--green-mid); border-radius: 50px; padding: 6px 16px; font-size: 13px; font-weight: 600; margin-bottom: 20px; border: 1px solid rgba(82,183,136,.3); }
    .hero-title { font-family: 'Playfair Display', serif; font-size: clamp(40px,5vw,68px); font-weight: 900; line-height: 1.1; color: var(--green-deep); margin-bottom: 24px; }
    .hero-title .accent { color: var(--amber); font-style: italic; }
    .hero-desc { font-size: 17px; color: var(--text-mid); line-height: 1.7; margin-bottom: 36px; max-width: 480px; }
    .hero-cta { display: flex; gap: 16px; flex-wrap: wrap; }
    .btn-primary { background: var(--green-mid); color: #fff; border: none; padding: 16px 32px; border-radius: 50px; font-family: 'DM Sans', sans-serif; font-size: 16px; font-weight: 600; cursor: pointer; transition: all .25s; box-shadow: 0 4px 20px rgba(45,106,79,.35); }
    .btn-primary:hover { background: var(--green-deep); transform: translateY(-2px); box-shadow: 0 8px 28px rgba(45,106,79,.4); }
    .btn-outline { background: transparent; color: var(--green-mid); border: 2px solid var(--green-mid); padding: 14px 28px; border-radius: 50px; font-family: 'DM Sans', sans-serif; font-size: 16px; font-weight: 600; cursor: pointer; transition: all .25s; }
    .btn-outline:hover { background: var(--green-mid); color: #fff; transform: translateY(-2px); }
    .hero-stats { display: flex; gap: 32px; margin-top: 40px; }
    .hero-stat { text-align: center; }
    .hero-stat-num { font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 900; color: var(--green-mid); }
    .hero-stat-label { font-size: 12px; color: var(--text-light); font-weight: 500; }
    .hero-image-wrap { position: relative; display: flex; justify-content: center; }
    .hero-fruit-circle {
      width: 440px; height: 440px; border-radius: 50%;
      background: linear-gradient(135deg, #d8f3dc, #b7e4c7);
      display: flex; align-items: center; justify-content: center;
      font-size: 180px; position: relative; animation: float 4s ease-in-out infinite;
      box-shadow: 0 20px 60px rgba(45,106,79,.2);
    }
    .hero-tag {
      position: absolute; background: #fff; border-radius: var(--radius-sm);
      padding: 10px 16px; box-shadow: var(--shadow-card); font-weight: 600; font-size: 13px;
      display: flex; align-items: center; gap: 8px;
    }
    .hero-tag.tag-1 { top: 40px; left: -20px; animation: float 3s .5s ease-in-out infinite; }
    .hero-tag.tag-2 { bottom: 60px; right: -30px; animation: float 3.5s 1s ease-in-out infinite; }
    .hero-tag.tag-3 { top: 50%; left: -50px; transform: translateY(-50%); animation: float 4s 1.5s ease-in-out infinite; }

    /* Live Banner */
    .live-banner { background: var(--green-bright); padding: 12px 0; }
    .live-inner { max-width: 1280px; margin: 0 auto; padding: 0 24px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
    .live-dot { width: 10px; height: 10px; background: #ff4d4d; border-radius: 50%; animation: pulse 1.5s infinite; display: inline-block; margin-right: 8px; }
    .live-text { font-weight: 600; font-size: 14px; color: var(--green-deep); }

    /* Section common */
    .section { padding: 96px 0; }
    .section-alt { background: var(--warm-white); }
    .container { max-width: 1280px; margin: 0 auto; padding: 0 24px; }
    .section-header { text-align: center; margin-bottom: 56px; }
    .section-tag { display: inline-block; background: rgba(82,183,136,.15); color: var(--green-mid); border-radius: 50px; padding: 5px 16px; font-size: 12px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 12px; }
    .section-title { font-family: 'Playfair Display', serif; font-size: clamp(28px,4vw,44px); font-weight: 900; color: var(--green-deep); margin-bottom: 12px; }
    .section-sub { font-size: 16px; color: var(--text-mid); max-width: 520px; margin: 0 auto; line-height: 1.65; }

    /* Categories */
    .categories-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 16px; }
    .category-card {
      background: #fff; border-radius: var(--radius); padding: 24px 16px; text-align: center;
      cursor: pointer; transition: all .25s; border: 2px solid transparent;
      box-shadow: var(--shadow-soft);
    }
    .category-card:hover, .category-card.active { border-color: var(--green-bright); transform: translateY(-4px); box-shadow: var(--shadow-card); }
    .category-card.active { background: var(--green-mid); }
    .category-card.active .cat-name { color: #fff; }
    .cat-emoji { font-size: 36px; margin-bottom: 10px; display: block; }
    .cat-name { font-size: 13px; font-weight: 600; color: var(--text-dark); }

    /* Products */
    .products-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 24px; }
    .product-card {
      background: #fff; border-radius: var(--radius); overflow: hidden;
      box-shadow: var(--shadow-soft); transition: all .3s; cursor: pointer;
      position: relative;
    }
    .product-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-card); }
    .product-img { height: 200px; display: flex; align-items: center; justify-content: center; font-size: 80px; position: relative; }
    .product-badge {
      position: absolute; top: 12px; left: 12px;
      background: var(--terracotta); color: #fff;
      border-radius: 50px; padding: 3px 10px; font-size: 11px; font-weight: 700;
    }
    .product-badge.organic { background: var(--green-mid); }
    .product-badge.new { background: var(--amber); color: var(--green-deep); }
    .product-info { padding: 18px; }
    .product-name { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; color: var(--green-deep); margin-bottom: 4px; }
    .product-origin { font-size: 12px; color: var(--text-light); margin-bottom: 12px; }
    .product-rating { display: flex; align-items: center; gap: 4px; margin-bottom: 12px; font-size: 13px; color: var(--amber); }
    .product-footer { display: flex; align-items: center; justify-content: space-between; }
    .product-price { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 900; color: var(--green-mid); }
    .product-price span { font-size: 13px; font-weight: 400; color: var(--text-light); text-decoration: line-through; margin-left: 4px; font-family: 'DM Sans', sans-serif; }
    .btn-add {
      background: var(--green-mid); color: #fff; border: none;
      border-radius: 50%; width: 38px; height: 38px; font-size: 18px;
      cursor: pointer; transition: all .2s; display: flex; align-items: center; justify-content: center;
    }
    .btn-add:hover { background: var(--green-deep); transform: scale(1.1); }
    .wishlist-btn {
      position: absolute; top: 12px; right: 12px;
      background: rgba(255,255,255,.9); border: none; border-radius: 50%;
      width: 34px; height: 34px; cursor: pointer; font-size: 16px;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 8px rgba(0,0,0,.1); transition: transform .2s;
    }
    .wishlist-btn:hover { transform: scale(1.15); }

    /* Banner */
    .promo-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
    .promo-card {
      border-radius: var(--radius); padding: 40px 36px; position: relative; overflow: hidden; cursor: pointer;
      transition: transform .3s;
    }
    .promo-card:hover { transform: scale(1.02); }
    .promo-card.green { background: linear-gradient(135deg, var(--green-deep), var(--green-mid)); color: #fff; }
    .promo-card.amber { background: linear-gradient(135deg, #7b4a00, var(--amber)); color: #fff; }
    .promo-emoji { font-size: 80px; position: absolute; right: 24px; bottom: 20px; opacity: .25; }
    .promo-tag-text { font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; opacity: .75; margin-bottom: 8px; }
    .promo-title { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 900; margin-bottom: 12px; }
    .promo-desc { font-size: 14px; opacity: .8; margin-bottom: 20px; line-height: 1.6; }
    .promo-btn { display: inline-block; background: rgba(255,255,255,.2); color: #fff; border: 1px solid rgba(255,255,255,.4); border-radius: 50px; padding: 10px 20px; font-size: 13px; font-weight: 600; cursor: pointer; transition: background .2s; }
    .promo-btn:hover { background: rgba(255,255,255,.35); }

    /* Testimonials */
    .testimonials-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
    .testi-card { background: #fff; border-radius: var(--radius); padding: 28px; box-shadow: var(--shadow-soft); }
    .testi-stars { color: var(--amber); font-size: 16px; margin-bottom: 14px; }
    .testi-text { font-size: 15px; color: var(--text-mid); line-height: 1.7; margin-bottom: 20px; font-style: italic; }
    .testi-user { display: flex; align-items: center; gap: 12px; }
    .testi-avatar { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; }
    .testi-name { font-weight: 700; font-size: 14px; color: var(--green-deep); }
    .testi-location { font-size: 12px; color: var(--text-light); }

    /* Features */
    .features-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
    .feature-card { text-align: center; padding: 32px 20px; background: #fff; border-radius: var(--radius); box-shadow: var(--shadow-soft); transition: transform .3s; }
    .feature-card:hover { transform: translateY(-5px); }
    .feature-icon { font-size: 40px; margin-bottom: 14px; display: block; animation: float 3s ease-in-out infinite; }
    .feature-title { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; color: var(--green-deep); margin-bottom: 8px; }
    .feature-desc { font-size: 13px; color: var(--text-mid); line-height: 1.6; }

    /* Cart Drawer */
    .cart-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 1998; opacity: 0; pointer-events: none; transition: opacity .3s; }
    .cart-overlay.open { opacity: 1; pointer-events: all; }
    .cart-drawer {
      position: fixed; right: 0; top: 0; bottom: 0; width: 380px; max-width: 100vw;
      background: #fff; z-index: 1999; transform: translateX(100%); transition: transform .35s cubic-bezier(.4,0,.2,1);
      display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(0,0,0,.15);
    }
    .cart-drawer.open { transform: translateX(0); }
    .cart-head { padding: 24px; border-bottom: 1px solid #eee; display: flex; align-items: center; justify-content: space-between; }
    .cart-head h2 { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 900; color: var(--green-deep); }
    .close-btn { background: none; border: none; font-size: 22px; cursor: pointer; color: var(--text-mid); }
    .cart-body { flex: 1; overflow-y: auto; padding: 20px; }
    .cart-item { display: flex; gap: 14px; margin-bottom: 18px; padding-bottom: 18px; border-bottom: 1px solid #f0f0f0; align-items: center; }
    .cart-item-emoji { font-size: 40px; width: 60px; height: 60px; background: var(--cream); border-radius: var(--radius-sm); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .cart-item-info { flex: 1; }
    .cart-item-name { font-weight: 700; font-size: 14px; color: var(--green-deep); margin-bottom: 4px; }
    .cart-item-price { font-size: 13px; color: var(--text-mid); }
    .qty-ctrl { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
    .qty-btn { width: 28px; height: 28px; border: 1px solid #ddd; border-radius: 50%; background: #fff; cursor: pointer; font-size: 14px; display: flex; align-items: center; justify-content: center; transition: all .2s; }
    .qty-btn:hover { background: var(--green-mid); color: #fff; border-color: var(--green-mid); }
    .qty-num { font-weight: 700; font-size: 14px; min-width: 20px; text-align: center; }
    .cart-remove { background: none; border: none; color: #ccc; cursor: pointer; font-size: 18px; transition: color .2s; }
    .cart-remove:hover { color: var(--terracotta); }
    .cart-foot { padding: 20px; border-top: 1px solid #eee; }
    .cart-total { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
    .cart-total-label { font-size: 15px; font-weight: 500; color: var(--text-mid); }
    .cart-total-value { font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 900; color: var(--green-mid); }
    .btn-checkout { width: 100%; background: var(--green-mid); color: #fff; border: none; padding: 16px; border-radius: var(--radius-sm); font-family: 'DM Sans', sans-serif; font-size: 16px; font-weight: 700; cursor: pointer; transition: all .25s; display: flex; align-items: center; justify-content: center; gap: 10px; }
    .btn-checkout:hover { background: var(--green-deep); transform: translateY(-1px); }

    /* Payment Modal */
    .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.55); z-index: 2100; display: flex; align-items: center; justify-content: center; padding: 20px; }
    .modal-box { background: #fff; border-radius: var(--radius); padding: 36px; max-width: 480px; width: 100%; max-height: 90vh; overflow-y: auto; }
    .modal-title { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 900; color: var(--green-deep); margin-bottom: 6px; }
    .modal-sub { font-size: 14px; color: var(--text-mid); margin-bottom: 28px; }
    .payment-methods { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; }
    .pay-method { padding: 14px; border: 2px solid #eee; border-radius: var(--radius-sm); cursor: pointer; text-align: center; transition: all .2s; }
    .pay-method:hover, .pay-method.active { border-color: var(--green-bright); background: rgba(82,183,136,.07); }
    .pay-method-icon { font-size: 24px; margin-bottom: 6px; display: block; }
    .pay-method-label { font-size: 12px; font-weight: 600; color: var(--text-dark); }
    .form-group { margin-bottom: 16px; }
    .form-label { display: block; font-size: 13px; font-weight: 600; color: var(--text-dark); margin-bottom: 6px; }
    .form-input { width: 100%; border: 1.5px solid #e0e0e0; border-radius: var(--radius-sm); padding: 12px 14px; font-family: 'DM Sans', sans-serif; font-size: 14px; outline: none; transition: border-color .2s; }
    .form-input:focus { border-color: var(--green-bright); }
    .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .modal-footer { display: flex; gap: 12px; margin-top: 24px; }
    .btn-cancel { flex: 1; background: #f5f5f5; color: var(--text-mid); border: none; padding: 14px; border-radius: var(--radius-sm); font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; }
    .btn-pay { flex: 2; background: var(--green-mid); color: #fff; border: none; padding: 14px; border-radius: var(--radius-sm); font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 700; cursor: pointer; transition: background .2s; }
    .btn-pay:hover { background: var(--green-deep); }
    .success-msg { text-align: center; padding: 20px 0; }
    .success-emoji { font-size: 64px; margin-bottom: 16px; animation: pulse 1s infinite; }
    .success-title { font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 900; color: var(--green-deep); margin-bottom: 8px; }
    .success-text { font-size: 15px; color: var(--text-mid); }
    .upi-logos { display: flex; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
    .upi-logo { background: #f5f5f5; border-radius: 8px; padding: 8px 14px; font-size: 12px; font-weight: 700; color: var(--text-dark); }

    /* Footer */
    .footer { background: var(--green-deep); color: rgba(255,255,255,.85); padding: 72px 0 32px; }
    .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 48px; }
    .footer-brand .logo-text { color: #fff; font-size: 26px; }
    .footer-desc { font-size: 14px; line-height: 1.7; margin-top: 12px; margin-bottom: 20px; opacity: .75; }
    .footer-contact { font-size: 14px; line-height: 1.8; opacity: .85; }
    .footer-heading { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; color: #fff; margin-bottom: 16px; }
    .footer-links { list-style: none; }
    .footer-links li { margin-bottom: 10px; }
    .footer-links a { color: rgba(255,255,255,.7); text-decoration: none; font-size: 14px; transition: color .2s; }
    .footer-links a:hover { color: var(--green-light); }
    .footer-bottom { border-top: 1px solid rgba(255,255,255,.1); padding-top: 24px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
    .footer-copy { font-size: 13px; opacity: .6; }
    .footer-payments { display: flex; gap: 8px; flex-wrap: wrap; }
    .pay-chip { background: rgba(255,255,255,.1); border-radius: 6px; padding: 5px 10px; font-size: 11px; font-weight: 700; }

    /* Toast */
    .toast-wrap { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 3000; display: flex; flex-direction: column; gap: 10px; align-items: center; pointer-events: none; }
    .toast { background: var(--green-deep); color: #fff; border-radius: 50px; padding: 12px 24px; font-size: 14px; font-weight: 600; box-shadow: 0 4px 20px rgba(0,0,0,.2); animation: fadeUp .3s ease; display: flex; align-items: center; gap: 8px; }

    /* Newsletter */
    .newsletter-wrap { background: linear-gradient(135deg, var(--green-mid), var(--green-deep)); border-radius: var(--radius); padding: 56px 48px; text-align: center; color: #fff; }
    .newsletter-title { font-family: 'Playfair Display', serif; font-size: 32px; font-weight: 900; margin-bottom: 12px; }
    .newsletter-sub { font-size: 16px; opacity: .8; margin-bottom: 28px; }
    .newsletter-form { display: flex; gap: 12px; max-width: 440px; margin: 0 auto; }
    .newsletter-input { flex: 1; border: none; border-radius: 50px; padding: 14px 20px; font-family: 'DM Sans', sans-serif; font-size: 14px; outline: none; }
    .newsletter-btn { background: var(--amber); color: var(--green-deep); border: none; border-radius: 50px; padding: 14px 24px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; transition: background .2s; white-space: nowrap; }
    .newsletter-btn:hover { background: var(--amber-light); }

    /* Search bar */
    .search-wrap { max-width: 540px; margin: 0 auto 40px; position: relative; }
    .search-input { width: 100%; border: 2px solid #e0e0e0; border-radius: 50px; padding: 14px 52px 14px 20px; font-family: 'DM Sans', sans-serif; font-size: 15px; outline: none; transition: border-color .2s; background: #fff; }
    .search-input:focus { border-color: var(--green-bright); }
    .search-icon { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); font-size: 20px; color: var(--text-light); }

    /* Responsive */
    @media (max-width: 900px) {
      .hero-inner { grid-template-columns: 1fr; text-align: center; }
      .hero-image-wrap { display: none; }
      .hero-cta { justify-content: center; }
      .hero-stats { justify-content: center; }
      .features-grid { grid-template-columns: repeat(2, 1fr); }
      .promo-grid { grid-template-columns: 1fr; }
      .footer-grid { grid-template-columns: 1fr 1fr; }
      .nav-links { display: none; }
    }
    @media (max-width: 600px) {
      .features-grid { grid-template-columns: 1fr; }
      .footer-grid { grid-template-columns: 1fr; }
      .newsletter-form { flex-direction: column; }
    }
  `}</style>
);

// ─── DATA ─────────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: "all", name: "All Items", emoji: "🌿" },
  { id: "vegetables", name: "Vegetables", emoji: "🥦" },
  { id: "fruits", name: "Fruits", emoji: "🍎" },
  { id: "grains", name: "Grains & Pulses", emoji: "🌾" },
  { id: "dairy", name: "Dairy", emoji: "🥛" },
  { id: "herbs", name: "Herbs & Spices", emoji: "🌱" },
  { id: "oils", name: "Oils & Ghee", emoji: "🫙" },
  { id: "snacks", name: "Snacks", emoji: "🍫" },
];

const PRODUCTS = [
  { id: 1, name: "Farm Fresh Spinach", origin: "Nashik, Maharashtra", emoji: "🥬", price: 49, mrp: 65, category: "vegetables", badge: "organic", rating: 4.8, reviews: 240, bg: "#e8f5e9" },
  { id: 2, name: "Organic Alphonso Mango", origin: "Ratnagiri, Maharashtra", emoji: "🥭", price: 199, mrp: 280, category: "fruits", badge: "new", rating: 5.0, reviews: 184, bg: "#fff8e1" },
  { id: 3, name: "A2 Desi Cow Milk", origin: "Anand, Gujarat", emoji: "🥛", price: 89, mrp: 110, category: "dairy", badge: "organic", rating: 4.9, reviews: 320, bg: "#f3e5f5" },
  { id: 4, name: "Red Rice (Chakara)", origin: "Kerala", emoji: "🍚", price: 159, mrp: 200, category: "grains", badge: null, rating: 4.7, reviews: 98, bg: "#fce4ec" },
  { id: 5, name: "Cold-Pressed Coconut Oil", origin: "Pollachi, TN", emoji: "🫙", price: 299, mrp: 380, category: "oils", badge: "organic", rating: 4.9, reviews: 411, bg: "#e0f2f1" },
  { id: 6, name: "Himalayan Turmeric", origin: "Uttarakhand", emoji: "🌿", price: 119, mrp: 160, category: "herbs", badge: "organic", rating: 4.8, reviews: 255, bg: "#fff9c4" },
  { id: 7, name: "Organic Dragon Fruit", origin: "Hyderabad, Telangana", emoji: "🐉", price: 249, mrp: 320, category: "fruits", badge: "new", rating: 4.6, reviews: 72, bg: "#fce4ec" },
  { id: 8, name: "Makhana (Fox Nuts)", origin: "Darbhanga, Bihar", emoji: "⚪", price: 179, mrp: 230, category: "snacks", badge: "organic", rating: 4.7, reviews: 163, bg: "#f5f5f5" },
  { id: 9, name: "Desi Ghee A2", origin: "Vrindavan, UP", emoji: "🧈", price: 699, mrp: 850, category: "oils", badge: "organic", rating: 5.0, reviews: 512, bg: "#fff3e0" },
  { id: 10, name: "Baby Brinjal", origin: "Amravati, Maharashtra", emoji: "🍆", price: 59, mrp: 80, category: "vegetables", badge: null, rating: 4.5, reviews: 88, bg: "#ede7f6" },
  { id: 11, name: "Black Sesame Seeds", origin: "Rajkot, Gujarat", emoji: "🫘", price: 89, mrp: 115, category: "grains", badge: "organic", rating: 4.8, reviews: 134, bg: "#efebe9" },
  { id: 12, name: "Dried Moringa Leaves", origin: "Coimbatore, TN", emoji: "🌿", price: 149, mrp: 200, category: "herbs", badge: "new", rating: 4.9, reviews: 207, bg: "#e8f5e9" },
];

const TESTIMONIALS = [
  { name: "Priya Sharma", location: "Delhi", rating: 5, text: "Absolutely love the quality! The A2 ghee is incredibly pure and aromatic. My family now orders weekly. Fast delivery and beautiful packaging too.", avatar: "👩🏽" },
  { name: "Arjun Mehta", location: "Mumbai", rating: 5, text: "I switched completely from supermarket groceries. The vegetables are so fresh — I can taste the difference in every meal. Highly recommend!", avatar: "👨🏽" },
  { name: "Fatima Khan", location: "Hyderabad", rating: 5, text: "Best organic store online! The turmeric from Uttarakhand is unlike anything I've tried before. 100% genuine products and great customer service.", avatar: "👩🏿" },
  { name: "Ravi Pillai", location: "Bangalore", rating: 5, text: "The Alphonso mangoes were divine — true Ratnagiri quality. Arrived within 2 days, perfectly packed. This is now my go-to for all organic needs.", avatar: "👨🏻" },
  { name: "Neha Gupta", location: "Pune", rating: 5, text: "Finally a trustworthy source for cold-pressed oils. The coconut oil smells like fresh coconuts. Reordering every month without fail!", avatar: "👩🏼" },
  { name: "Vikram Singh", location: "Jaipur", rating: 4, text: "Great product range and quick delivery. The makhana is my new favourite snack. Love that everything comes directly from farmers!", avatar: "👨🏽" },
];

// ─── COMPONENTS ───────────────────────────────────────────────────────────────
const StarRating = ({ rating }) => (
  <div className="product-rating">
    {"★".repeat(Math.floor(rating))}{"☆".repeat(5 - Math.floor(rating))}
    <span style={{ color: "#888", fontFamily: "'DM Sans', sans-serif" }}>{rating} ({Math.floor(Math.random() * 300 + 80)})</span>
  </div>
);

const Toast = ({ toasts }) => (
  <div className="toast-wrap">
    {toasts.map(t => (
      <div key={t.id} className="toast">{t.emoji} {t.msg}</div>
    ))}
  </div>
);

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [cartBounce, setCartBounce] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const [payStep, setPayStep] = useState("form"); // "form" | "success"
  const [payMethod, setPayMethod] = useState("upi");
  const [email, setEmail] = useState("");
  const [liveOrders, setLiveOrders] = useState(847);
  const [liveUsers, setLiveUsers] = useState(234);

  // Scroll listener
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Live counter simulation
  useEffect(() => {
    const iv = setInterval(() => {
      setLiveOrders(n => n + Math.floor(Math.random() * 3));
      setLiveUsers(n => Math.max(200, n + (Math.random() > .5 ? 1 : -1)));
    }, 4000);
    return () => clearInterval(iv);
  }, []);

  const addToast = (msg, emoji = "✅") => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, emoji }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 2500);
  };

  const addToCart = (product) => {
    setCart(c => {
      const ex = c.find(i => i.id === product.id);
      if (ex) return c.map(i => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { ...product, qty: 1 }];
    });
    setCartBounce(true);
    setTimeout(() => setCartBounce(false), 600);
    addToast(`${product.name} added to cart`, product.emoji);
  };

  const updateQty = (id, delta) => {
    setCart(c => c.map(i => i.id === id ? { ...i, qty: Math.max(0, i.qty + delta) } : i).filter(i => i.qty > 0));
  };

  const toggleWishlist = (id) => {
    setWishlist(w => w.includes(id) ? w.filter(x => x !== id) : [...w, id]);
    addToast(wishlist.includes(id) ? "Removed from wishlist" : "Added to wishlist", "💚");
  };

  const cartCount = cart.reduce((a, i) => a + i.qty, 0);
  const cartTotal = cart.reduce((a, i) => a + i.price * i.qty, 0);
  const discount = cart.reduce((a, i) => a + (i.mrp - i.price) * i.qty, 0);

  const filtered = PRODUCTS.filter(p =>
    (activeCategory === "all" || p.category === activeCategory) &&
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCheckout = () => { setPayModal(true); setCartOpen(false); };
  const handlePay = (e) => { e.preventDefault(); setPayStep("success"); };

  const tickerItems = [
    "🌿 Free delivery on orders above ₹499",
    "🥗 100% Organic & Certified",
    "🚜 Direct from farmers",
    "🌱 No pesticides, no preservatives",
    "📦 Same-day dispatch before 2 PM",
    "💚 Save up to 40% vs retail prices",
  ];

  return (
    <>
      <GlobalStyles />

      {/* Ticker */}
      <div className="ticker-wrap">
        <div className="ticker-track">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="ticker-item">• {item}</span>
          ))}
        </div>
      </div>

      {/* NAV */}
      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-inner">
          <a href="#" className="logo">
            <div className="logo-icon">🌿</div>
            <span className="logo-text">Organic<span>Foods</span></span>
          </a>
          <ul className="nav-links">
            {["Home", "Shop", "About", "Blog", "Contact"].map(l => (
              <li key={l}><a href="#shop">{l}</a></li>
            ))}
          </ul>
          <div className="nav-actions">
            <button className={`btn-cart ${cartBounce ? "bounce" : ""}`} onClick={() => setCartOpen(true)}>
              <span className="cart-icon">🛒</span> Cart
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-blob" />
        <div className="hero-inner">
          <div className="animate-fade-up">
            <div className="hero-badge">🌱 Certified Organic Produce</div>
            <h1 className="hero-title">
              Nature's Best,<br />
              <span className="accent">Delivered Fresh</span><br />
              to Your Door
            </h1>
            <p className="hero-desc">
              India's most trusted organic food marketplace. Sourced directly from certified farmers across 18 states — no middlemen, no chemicals, just pure goodness.
            </p>
            <div className="hero-cta">
              <button className="btn-primary" onClick={() => document.getElementById("shop").scrollIntoView({ behavior: "smooth" })}>Shop Now 🛒</button>
              <button className="btn-outline">Explore Farms 🌾</button>
            </div>
            <div className="hero-stats">
              {[
                { num: "50K+", label: "Happy Customers" },
                { num: "200+", label: "Organic Products" },
                { num: "18", label: "States Covered" },
                { num: "4.9★", label: "Avg Rating" },
              ].map(s => (
                <div className="hero-stat" key={s.label}>
                  <div className="hero-stat-num">{s.num}</div>
                  <div className="hero-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="hero-image-wrap animate-fade-up delay-3">
            <div className="hero-fruit-circle">🌿</div>
            <div className="hero-tag tag-1">🥭 Alphonso Mango</div>
            <div className="hero-tag tag-2">🥛 A2 Cow Milk</div>
            <div className="hero-tag tag-3">🌾 Red Rice</div>
          </div>
        </div>
      </section>

      {/* LIVE BANNER */}
      <div className="live-banner">
        <div className="live-inner">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span className="live-dot" />
            <span className="live-text">🔴 LIVE — {liveUsers} people shopping right now</span>
          </div>
          <span className="live-text">🚀 {liveOrders.toLocaleString()} orders delivered this week</span>
          <span className="live-text">⚡ Next dispatch in 2h 34m</span>
        </div>
      </div>

      {/* FEATURES */}
      <section className="section section-alt">
        <div className="container">
          <div className="features-grid">
            {[
              { icon: "🚛", title: "Fast Delivery", desc: "Same-day dispatch on orders placed before 2 PM. Pan-India coverage with cold chain logistics." },
              { icon: "🌱", title: "100% Organic", desc: "NPOP & FSSAI certified. Every product traceable back to the farm it came from." },
              { icon: "💰", title: "Farm Prices", desc: "No middlemen means you save up to 40% vs retail. Better price for farmers too." },
              { icon: "🔄", title: "Easy Returns", desc: "Not satisfied? We offer a 100% replacement or refund guarantee within 24 hours." },
            ].map((f, i) => (
              <div className={`feature-card animate-fade-up delay-${i + 1}`} key={f.title}>
                <span className="feature-icon">{f.icon}</span>
                <div className="feature-title">{f.title}</div>
                <div className="feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOP */}
      <section className="section" id="shop">
        <div className="container">
          <div className="section-header animate-fade-up">
            <span className="section-tag">🌿 Fresh Stock</span>
            <h2 className="section-title">Shop Organic Goodness</h2>
            <p className="section-sub">Hand-picked from certified organic farms across India. All products are fresh, natural, and chemical-free.</p>
          </div>

          {/* Search */}
          <div className="search-wrap animate-fade-up delay-1">
            <input
              className="search-input"
              placeholder="Search products… e.g. turmeric, ghee, mango"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>

          {/* Categories */}
          <div className="categories-grid animate-fade-up delay-2" style={{ marginBottom: "40px" }}>
            {CATEGORIES.map(cat => (
              <div
                key={cat.id}
                className={`category-card ${activeCategory === cat.id ? "active" : ""}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                <span className="cat-emoji">{cat.emoji}</span>
                <span className="cat-name">{cat.name}</span>
              </div>
            ))}
          </div>

          {/* Products */}
          <div className="products-grid">
            {filtered.map((p, i) => (
              <div className={`product-card animate-fade-up delay-${(i % 4) + 1}`} key={p.id} style={{ "--card-bg": p.bg }}>
                <div className="product-img" style={{ background: p.bg }}>
                  <span>{p.emoji}</span>
                  {p.badge && <span className={`product-badge ${p.badge}`}>{p.badge === "organic" ? "🌿 Organic" : p.badge === "new" ? "✨ New" : `${Math.round((1 - p.price / p.mrp) * 100)}% OFF`}</span>}
                  <button className="wishlist-btn" onClick={() => toggleWishlist(p.id)}>
                    {wishlist.includes(p.id) ? "❤️" : "🤍"}
                  </button>
                </div>
                <div className="product-info">
                  <div className="product-name">{p.name}</div>
                  <div className="product-origin">📍 {p.origin}</div>
                  <StarRating rating={p.rating} />
                  <div className="product-footer">
                    <div className="product-price">
                      ₹{p.price}
                      <span>₹{p.mrp}</span>
                    </div>
                    <button className="btn-add" onClick={() => addToCart(p)}>+</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-light)", fontSize: "16px" }}>
              🌿 No products found for "{search}"
            </div>
          )}
        </div>
      </section>

      {/* PROMO BANNERS */}
      <section className="section section-alt">
        <div className="container">
          <div className="promo-grid">
            <div className="promo-card green">
              <span className="promo-emoji">🥗</span>
              <div className="promo-tag-text">Limited Offer</div>
              <div className="promo-title">Veggie Box<br />Subscription</div>
              <div className="promo-desc">Get 10 seasonal organic vegetables delivered weekly. Save 30% vs individual purchases.</div>
              <span className="promo-btn">Subscribe Now →</span>
            </div>
            <div className="promo-card amber">
              <span className="promo-emoji">🍯</span>
              <div className="promo-tag-text">Bestseller Bundle</div>
              <div className="promo-title">Desi Ghee &<br />Honey Combo</div>
              <div className="promo-desc">Pure A2 cow ghee + raw forest honey from Uttarakhand. The ultimate wellness duo at ₹999.</div>
              <span className="promo-btn">Grab the Deal →</span>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">💬 Reviews</span>
            <h2 className="section-title">What Our Customers Say</h2>
            <p className="section-sub">Trusted by 50,000+ families across India who made the switch to organic living.</p>
          </div>
          <div className="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div className={`testi-card animate-fade-up delay-${(i % 3) + 1}`} key={t.name}>
                <div className="testi-stars">{"★".repeat(t.rating)}</div>
                <div className="testi-text">"{t.text}"</div>
                <div className="testi-user">
                  <div className="testi-avatar" style={{ background: "var(--green-light)" }}>{t.avatar}</div>
                  <div>
                    <div className="testi-name">{t.name}</div>
                    <div className="testi-location">📍 {t.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="section section-alt">
        <div className="container">
          <div className="newsletter-wrap animate-fade-up">
            <div className="newsletter-title">🌿 Join the Organic Movement</div>
            <div className="newsletter-sub">Get exclusive deals, seasonal recipes, and farm updates. No spam, ever.</div>
            <div className="newsletter-form">
              <input
                className="newsletter-input"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <button className="newsletter-btn" onClick={() => { if (email) { addToast("Subscribed! 🎉", "📧"); setEmail(""); } }}>
                Subscribe Free
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="logo">
                <div className="logo-icon">🌿</div>
                <span className="logo-text">Organic<span style={{ color: "var(--amber)" }}>Foods</span></span>
              </div>
              <p className="footer-desc">
                India's most trusted organic marketplace. We bridge the gap between certified organic farmers and health-conscious families, ensuring purity at every step.
              </p>
              <div className="footer-contact">
                <div>🌐 downtoearth.food</div>
                <div>📞 +91 63981 99363</div>
                <div>👤 Mohd Owais Khan</div>
                <div>📧 hello@downtoearth.food</div>
              </div>
            </div>
            <div>
              <div className="footer-heading">Shop</div>
              <ul className="footer-links">
                {["Vegetables", "Fruits", "Dairy & Eggs", "Grains & Pulses", "Herbs & Spices", "Oils & Ghee", "Organic Snacks"].map(l => (
                  <li key={l}><a href="#shop">{l}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="footer-heading">Company</div>
              <ul className="footer-links">
                {["About Us", "Our Farmers", "Blog & Recipes", "Careers", "Press Kit", "Sustainability"].map(l => (
                  <li key={l}><a href="#">{l}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="footer-heading">Support</div>
              <ul className="footer-links">
                {["FAQ", "Shipping Policy", "Return Policy", "Track Order", "Partner With Us", "Privacy Policy"].map(l => (
                  <li key={l}><a href="#">{l}</a></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-copy">© 2025 OrganicFoods · downtoearth.food · All rights reserved</div>
            <div className="footer-payments">
              {["UPI", "PhonePe", "GPay", "Paytm", "RazorPay", "COD", "Visa", "Mastercard"].map(p => (
                <span className="pay-chip" key={p}>{p}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* ── CART DRAWER ── */}
      <div className={`cart-overlay ${cartOpen ? "open" : ""}`} onClick={() => setCartOpen(false)} />
      <div className={`cart-drawer ${cartOpen ? "open" : ""}`}>
        <div className="cart-head">
          <h2>🛒 Your Cart ({cartCount})</h2>
          <button className="close-btn" onClick={() => setCartOpen(false)}>✕</button>
        </div>
        <div className="cart-body">
          {cart.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-light)" }}>
              <div style={{ fontSize: "60px", marginBottom: "16px" }}>🛒</div>
              <div style={{ fontSize: "16px", fontWeight: 600 }}>Your cart is empty</div>
              <div style={{ fontSize: "13px", marginTop: "8px" }}>Add some fresh organic products!</div>
            </div>
          ) : (
            cart.map(item => (
              <div className="cart-item" key={item.id}>
                <div className="cart-item-emoji">{item.emoji}</div>
                <div className="cart-item-info">
                  <div className="cart-item-name">{item.name}</div>
                  <div className="cart-item-price">₹{item.price} each</div>
                  <div className="qty-ctrl">
                    <button className="qty-btn" onClick={() => updateQty(item.id, -1)}>−</button>
                    <span className="qty-num">{item.qty}</span>
                    <button className="qty-btn" onClick={() => updateQty(item.id, +1)}>+</button>
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--green-mid)", marginBottom: "8px" }}>₹{item.price * item.qty}</div>
                  <button className="cart-remove" onClick={() => updateQty(item.id, -item.qty)}>🗑</button>
                </div>
              </div>
            ))
          )}
        </div>
        {cart.length > 0 && (
          <div className="cart-foot">
            <div style={{ fontSize: "13px", color: "var(--green-mid)", marginBottom: "12px", fontWeight: 600 }}>
              🎉 You save ₹{discount} on this order!
            </div>
            <div className="cart-total">
              <span className="cart-total-label">Total Amount</span>
              <span className="cart-total-value">₹{cartTotal}</span>
            </div>
            <button className="btn-checkout" onClick={handleCheckout}>
              🔒 Secure Checkout →
            </button>
          </div>
        )}
      </div>

      {/* ── PAYMENT MODAL ── */}
      {payModal && (
        <div className="modal-overlay" onClick={() => { setPayModal(false); setPayStep("form"); }}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            {payStep === "success" ? (
              <div className="success-msg">
                <div className="success-emoji">🎉</div>
                <div className="success-title">Order Placed!</div>
                <div className="success-text">
                  Thank you for your order! You'll receive a confirmation on WhatsApp shortly.<br />
                  <strong>Expected delivery: 24–48 hours.</strong>
                </div>
                <button className="btn-primary" style={{ marginTop: "24px", width: "100%" }} onClick={() => { setPayModal(false); setPayStep("form"); setCart([]); }}>
                  Continue Shopping 🌿
                </button>
              </div>
            ) : (
              <>
                <div className="modal-title">🔒 Secure Checkout</div>
                <div className="modal-sub">Total: ₹{cartTotal} · {cartCount} items · Free delivery</div>

                <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-mid)", marginBottom: "10px" }}>Choose Payment Method</div>
                <div className="payment-methods">
                  {[
                    { id: "upi", icon: "📱", label: "UPI / QR" },
                    { id: "card", icon: "💳", label: "Credit/Debit Card" },
                    { id: "nb", icon: "🏦", label: "Net Banking" },
                    { id: "cod", icon: "💵", label: "Cash on Delivery" },
                  ].map(m => (
                    <div key={m.id} className={`pay-method ${payMethod === m.id ? "active" : ""}`} onClick={() => setPayMethod(m.id)}>
                      <span className="pay-method-icon">{m.icon}</span>
                      <div className="pay-method-label">{m.label}</div>
                    </div>
                  ))}
                </div>

                {payMethod === "upi" && (
                  <div>
                    <div className="upi-logos">
                      {["PhonePe", "GPay", "Paytm", "BHIM"].map(u => <span className="upi-logo" key={u}>{u}</span>)}
                    </div>
                    <div className="form-group" style={{ marginTop: "16px" }}>
                      <label className="form-label">UPI ID</label>
                      <input className="form-input" placeholder="yourname@upi" />
                    </div>
                  </div>
                )}

                {payMethod === "card" && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Card Number</label>
                      <input className="form-input" placeholder="1234 5678 9012 3456" maxLength={19} />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Expiry</label>
                        <input className="form-input" placeholder="MM/YY" maxLength={5} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">CVV</label>
                        <input className="form-input" placeholder="•••" maxLength={3} type="password" />
                      </div>
                    </div>
                  </>
                )}

                {payMethod === "cod" && (
                  <div style={{ background: "#f9f9f9", borderRadius: "10px", padding: "14px", fontSize: "14px", color: "var(--text-mid)", margin: "8px 0" }}>
                    💵 Pay ₹{cartTotal} cash when your order arrives. COD available across India.
                  </div>
                )}

                <div className="form-group" style={{ marginTop: "12px" }}>
                  <label className="form-label">Delivery Address</label>
                  <input className="form-input" placeholder="Full address with PIN code" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input className="form-input" placeholder="+91 XXXXX XXXXX" />
                </div>

                <div className="modal-footer">
                  <button className="btn-cancel" onClick={() => setPayModal(false)}>← Back</button>
                  <button className="btn-pay" onClick={handlePay}>
                    🔒 Pay ₹{cartTotal}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* TOAST */}
      <Toast toasts={toasts} />
    </>
  );
}
